import React, { useContext, useEffect, useState } from "react";
import { PlusCircleFilled } from "@ant-design/icons";
import styles from "./styles/AssetManagement.module.css";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import {
  MAX_OWNERS,
  TOTAL_OWNERSHIP,
  makeOwner,
} from "../Asset Evaluation/AssetEvaluation";
import { OWNER_CUSTOM } from "../../../Utils/assetEvaluationUtils";

/*
  Ownership is the one cell that cannot be edited inline: the percentages have
  to add up to 100 across the owner and everyone else, which needs the whole
  set visible at once. Clicking the cell opens this instead.

  It mirrors the "Other owners of this asset" block in the Add / Edit Asset
  modal: same owner dropdown, same 10-owner ceiling, same two rules (every row
  needs an owner, and the percentages must total 100). The last owner cannot be
  removed here either — dropping the block entirely is still the Edit Asset
  modal's job.
*/

const FALLBACK = {
  assetManagementText14: "Edit ownership",
  assetManagementText37: "Clear ownership",
  assetManagementText16: "Other owners",
  assetManagementText17: "Save",
  assetManagementText18: "Cancel",
  assetManagementText19:
    "Error: Total ownership percentage should reach 100%",
  assetManagementText20: "My Ownership (%)",
};

const EVAL_FALLBACK = {
  assetEvaluationText24: "Custom",
  assetEvaluationText38: "Owner n.",
  assetEvaluationText39: "Ownership (%)",
  assetEvaluationText40: "Add More Owners",
  assetEvaluationText42: "Remove",
  assetEvaluationText43: "Owner",
  assetEvaluationText44: "One,Two,Three,Four,Five,Six,Seven,Eight,Nine,Ten",
  assetEvaluationText45: "Error: Please select an owner for every row",
};

function parsePercent(raw) {
  const cleaned = String(raw).replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  return parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];
}

const displayPercent = (raw) => (raw === "" ? "" : `${raw}%`);

/*
  The "%" is rendered, never typed — `parsePercent` strips it back out of every
  keystroke. That makes Backspace look broken when the caret sits after it: the
  press deletes the "%", the strip removes it anyway, the value is unchanged,
  and the suffix is re-rendered. Nothing appears to happen, and the only way
  through was to move the caret in front of the sign first.

  So a delete that removed ONLY the suffix is read as a delete of the digit in
  front of it, which is what was meant. The caret is then parked before the "%"
  so the next press behaves like an ordinary one.
*/
const onPercentChange = (event, current, apply) => {
  const input = event.currentTarget;
  const typed = input.value;
  const shown = displayPercent(current);
  const suffixOnly = shown !== "" && typed === shown.slice(0, -1);
  const digits = suffixOnly
    ? parsePercent(typed).slice(0, -1)
    : parsePercent(typed);
  apply(digits);
  window.requestAnimationFrame(() => {
    if (document.activeElement !== input) return;
    input.setSelectionRange(digits.length, digits.length);
  });
};

/*
  Clicking or tabbing in puts the caret at the end, which is past the "%".
  Pulled back in front of it, so the field behaves the same however it was
  entered.
*/
const keepCaretBeforeSuffix = (event) => {
  const input = event.currentTarget;
  const limit = Math.max(0, input.value.length - 1);
  window.requestAnimationFrame(() => {
    if (document.activeElement !== input) return;
    if (input.selectionStart > limit || input.selectionEnd > limit) {
      input.setSelectionRange(
        Math.min(input.selectionStart, limit),
        Math.min(input.selectionEnd, limit)
      );
    }
  });
};

function OwnershipModal({ entry, ownerOptions = [], onClose, onSave, onClear }) {
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
      FALLBACK
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
        FALLBACK
    );
  }, [websiteCurrentLang]);
  const t = (key) => (content && content[key]) || FALLBACK[key];

  // The owner-row copy belongs to the evaluation section, so it reads the same
  // strings the Add / Edit Asset modal does.
  const tEval = (key) => {
    const evaluationContent =
      websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation;
    return (
      (evaluationContent && evaluationContent[key]) || EVAL_FALLBACK[key]
    );
  };

  const ownerWords = String(tEval("assetEvaluationText44") || "").split(",");
  const ownerPlaceholder = (i) =>
    `${tEval("assetEvaluationText43")} ${ownerWords[i] || i + 1}`;

  const [mine, setMine] = useState(
    entry && entry.my_ownership != null ? String(entry.my_ownership) : ""
  );
  /*
    An asset that has never had an owners block opens with one blank row, the
    same starting state as a fresh block in the Add Asset modal.
  */
  const [owners, setOwners] = useState(() => {
    const existing = (entry && entry.owners) || [];
    if (existing.length === 0) return [makeOwner()];
    return existing.map((o) => ({
      ...o,
      ownership: o.ownership != null ? String(o.ownership) : "",
    }));
  });
  const [showError, setShowError] = useState(false);

  const total = [mine, ...owners.map((o) => o.ownership)].reduce(
    (sum, v) => sum + (v === "" || v === "." ? 0 : Number(v)),
    0
  );
  const rounded = Math.round(total * 100) / 100;
  const totalValid = rounded === TOTAL_OWNERSHIP;
  /*
    A row is only complete once it names someone. "Custom" on its own does not:
    it is the prompt for the free-text field beside it, so that field has to
    carry a name too.
  */
  const ownersChosen = owners.every(
    (o) =>
      o.owner &&
      (o.owner !== OWNER_CUSTOM ||
        String(o.owner_custom_label || "").trim() !== "")
  );
  const valid = totalValid && ownersChosen;

  const patchOwner = (id, patch) => {
    setOwners((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
    setShowError(false);
  };

  const addOwner = () => {
    if (owners.length >= MAX_OWNERS) return;
    setOwners((prev) => [...prev, makeOwner()]);
    setShowError(false);
  };

  // Never removes the last owner — one is always required, as in the modal.
  const removeOwner = (id) => {
    setOwners((prev) => (prev.length <= 1 ? prev : prev.filter((o) => o.id !== id)));
    setShowError(false);
  };

  /*
    Whether the asset already carries an ownership block. Nothing to clear when
    it does not, and the button would then read as an action with no effect.
  */
  const hasSaved =
    Boolean(entry && String(entry.my_ownership ?? "").trim() !== "") ||
    ((entry && entry.owners) || []).some((o) => o && o.owner);

  const commit = () => {
    if (!valid) {
      setShowError(true);
      return;
    }
    onSave({ my_ownership: mine, owners });
  };

  return (
    <div className={styles.modal_backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-label={t("assetManagementText14")}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className={styles.modal_title}>{t("assetManagementText14")}</h3>

        <label className={styles.modal_field}>
          <span className={styles.modal_label}>
            {t("assetManagementText20")}
          </span>
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            className={`${styles.modal_input} ${
              showError && !totalValid ? styles.modal_input_invalid : ""
            }`}
            placeholder="0%"
            value={displayPercent(mine)}
            onFocus={keepCaretBeforeSuffix}
            onClick={keepCaretBeforeSuffix}
            onChange={(e) =>
              onPercentChange(e, mine, (digits) => {
                setMine(digits);
                setShowError(false);
              })
            }
          />
        </label>

        <div className={styles.modal_section}>
          {t("assetManagementText16")}
        </div>

        {owners.map((owner, index) => (
          <div key={owner.id || index} className={styles.modal_owner}>
            <div className={styles.modal_owner_field}>
              <span className={styles.modal_owner_label}>
                {`${tEval("assetEvaluationText38")}${index + 2}`}
              </span>
              {/*
                Custom replaces the dropdown with a field to name the owner in,
                the way picking Custom replaces the asset-type dropdown. The ✕
                goes back to the list. The row's own value holds the mode, so
                there is no extra per-row state to keep in step.
              */}
              {owner.owner === OWNER_CUSTOM ? (
                <div className={styles.custom_owner_wrap}>
                  <input
                    autoFocus
                    type="text"
                    className={`${styles.modal_select} ${
                      showError && !String(owner.owner_custom_label || "").trim()
                        ? styles.modal_input_invalid
                        : ""
                    }`}
                    placeholder={tEval("assetEvaluationText24")}
                    value={owner.owner_custom_label || ""}
                    onChange={(e) =>
                      patchOwner(owner.id, {
                        owner_custom_label: e.target.value,
                      })
                    }
                  />
                  {/* Back to the dropdown, discarding the name. */}
                  <button
                    type="button"
                    className={styles.custom_owner_clear}
                    aria-label={tEval("assetEvaluationText24")}
                    onClick={() =>
                      patchOwner(owner.id, {
                        owner: "",
                        owner_custom_label: "",
                      })
                    }
                  />
                </div>
              ) : (
              <select
                className={`${styles.modal_select} ${
                  showError && !owner.owner ? styles.modal_input_invalid : ""
                }`}
                value={owner.owner || ""}
                onChange={(e) => patchOwner(owner.id, { owner: e.target.value })}
              >
                <option value="">{ownerPlaceholder(index)}</option>
                {/*
                  Plain names: an owner is stored as the name itself, so the
                  option's value and its label are the same string.
                */}
                {ownerOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value={OWNER_CUSTOM}>
                  {tEval("assetEvaluationText24")}
                </option>
              </select>
              )}
            </div>

            <div className={styles.modal_owner_pct}>
              <span className={styles.modal_owner_label}>
                {tEval("assetEvaluationText39")}
              </span>
              <input
                type="text"
                inputMode="decimal"
                className={`${styles.modal_input} ${
                  showError && !totalValid ? styles.modal_input_invalid : ""
                }`}
                placeholder="0%"
                value={displayPercent(owner.ownership)}
                onFocus={keepCaretBeforeSuffix}
                onClick={keepCaretBeforeSuffix}
                onChange={(e) =>
                  onPercentChange(e, owner.ownership, (digits) =>
                    patchOwner(owner.id, { ownership: digits })
                  )
                }
              />
            </div>

            {/* Only owners beyond the first can be removed. */}
            <div className={styles.modal_owner_del}>
              {index > 0 && (
                <button
                  type="button"
                  className={styles.modal_owner_delete}
                  title={tEval("assetEvaluationText42")}
                  aria-label={tEval("assetEvaluationText42")}
                  onClick={() => removeOwner(owner.id)}
                />
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.modal_add_owner}
          disabled={owners.length >= MAX_OWNERS}
          onClick={addOwner}
        >
          <PlusCircleFilled className={styles.modal_add_owner_icon} />
          <span>{tEval("assetEvaluationText40")}</span>
        </button>

        {showError && !ownersChosen && (
          <div className={styles.modal_error}>
            {tEval("assetEvaluationText45")}
          </div>
        )}

        {showError && !totalValid && (
          <div className={styles.modal_error}>
            {t("assetManagementText19")}
          </div>
        )}

        <div className={styles.modal_actions}>
          {/*
            Clears the ownership block outright, the way removing the "Other
            owners of this asset" field does in the Add / Edit Asset modal.
            Offered only when there is something to clear, and left of the other
            two so it cannot be mistaken for Cancel.
          */}
          {onClear && hasSaved && (
            <button
              type="button"
              className={styles.modal_clear}
              onClick={onClear}
            >
              {t("assetManagementText37")}
            </button>
          )}
          <button
            type="button"
            className={styles.modal_cancel}
            onClick={onClose}
          >
            {t("assetManagementText18")}
          </button>
          <button
            type="button"
            className={styles.modal_save}
            onClick={commit}
          >
            {t("assetManagementText17")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OwnershipModal;
